import { useEffect, useId, useState } from "react";
import { Field, type FieldInputProps } from "react-final-form";
import type { FieldOption, ValidatorFn } from "../../types/form";
import { composeValidators } from "../../utils";
import { Stack } from "../stack";
import { Checkbox } from "./components/checkbox";
import { Fieldset } from "./components/fieldset";
import { Legend } from "./components/legend";
import { FormControlLabel } from "./form-control-label";
import { FormHelperText } from "./form-helper-text";

type Props = {
	options: Array<FieldOption>;
	name: string;
	validators?: Array<ValidatorFn<Array<string>>>;
	legend?: string;
	initialValue?: Array<string>;
	disabledFields?: Array<string>;
};

const getIsChecked = (
	{ value }: FieldInputProps<Array<string>, HTMLElement>,
	option: FieldOption,
) => !!value && value.some((v) => v === option.value);

const onCheckboxChange = (
	{ onChange, value: values }: FieldInputProps<Array<string>, HTMLElement>,
	checked: boolean,
	option: FieldOption,
) => {
	const newValues = checked ? [option.value, ...values] : values.filter((v) => v !== option.value);
	onChange(newValues);
};

export const CheckboxGroupField = ({
	name,
	legend,
	options,
	initialValue,
	validators,
	disabledFields = [],
}: Props) => {
	const describedById = useId();
	const [hasSubmitFailed, setHasSubmitError] = useState(false);
	return (
		<Fieldset
			aria-invalid={hasSubmitFailed}
			aria-describedby={hasSubmitFailed ? describedById : undefined}
		>
			{!!legend && <Legend>{legend}</Legend>}
			<Field<Array<string>>
				name={name}
				initialValue={initialValue}
				format={(v) => v || []} // defaults the field to empty array instead of empty string
				validate={validators && composeValidators(...validators)}
			>
				{({ input, meta }) => {
					// biome-ignore lint/correctness/useHookAtTopLevel: Not conditional
					useEffect(() => {
						setHasSubmitError(!!meta.submitFailed && !!meta.error);
					}, [meta]);

					return (
						<Stack gap={4}>
							{options.map((option) => (
								<FormControlLabel label={option.label} key={option.value}>
									<Checkbox
										checked={getIsChecked(input, option)}
										name={input.name}
										value={option.value}
										onChange={(e) => {
											onCheckboxChange(input, !!e.target.checked, option);
										}}
										disabled={disabledFields.some((value) => value === option.value)}
									/>
								</FormControlLabel>
							))}
							{meta.error && meta.touched && (
								<FormHelperText error id={describedById}>
									{meta.error}
								</FormHelperText>
							)}
						</Stack>
					);
				}}
			</Field>
		</Fieldset>
	);
};
